import React, { useState, useEffect } from "react";
import "./ProfilePage.css";
import { getToken, getUserId } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";

const ProfilePage = () => {
  const [profileData, setProfileData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ================= FETCH PROFILE =================
  useEffect(() => {
    const fetchProfile = async () => {
      const token = getToken();
      const userId = getUserId();

      if (!token || !userId) {
        setError("You are not logged in.");
        return;
      }

      try {
        const profile = await apiClient(
          `/auth/employees/${userId}/`
        );

        if (!profile) {
          throw new Error("No profile data received");
        }

        setProfileData(profile);
        setEditableData({
          address: profile.address || "",
          secondary_phone: profile.secondary_phone || "",
          emergency_contact: profile.emergency_contact || "",
        });
      } catch (err) {
        console.error(err);
        setError("Failed to fetch profile details");
      }
    };

    fetchProfile();
  }, []);

  // ================= EDIT =================
  const handleEditClick = () => {
    setIsEditing(true);
    setSuccessMsg("");
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setEditableData({
      address: profileData.address || "",
      secondary_phone: profileData.secondary_phone || "",
      emergency_contact: profileData.emergency_contact || "",
    });
  };

  // ================= INPUT CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only numeric input for phone fields
    if (
      (name === "secondary_phone" || name === "emergency_contact") &&
      !/^\d*$/.test(value)
    ) {
      return;
    }

    setEditableData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================= SAVE =================
  const handleSave = async () => {
    const { secondary_phone, emergency_contact } = editableData;

    // Validation
    if (
      (secondary_phone && secondary_phone.length !== 10) ||
      (emergency_contact && emergency_contact.length !== 10)
    ) {
      setError("Phone numbers must be exactly 10 digits.");
      return;
    }

    const userId = getUserId();
    if (!userId) return;

    try {
      const updatedProfile = await apiClient(
        `/auth/employees/${userId}/`,
        {
          method: "PUT",
          body: JSON.stringify({
            ...profileData,
            ...editableData,
          }),
        }
      );

      if (!updatedProfile) {
        throw new Error("Update failed");
      }

      setProfileData(updatedProfile);
      setIsEditing(false);
      setSuccessMsg("Profile updated successfully!");
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to update profile");
    }
  };

  // ================= STATES =================
  if (error && !isEditing) {
    return <div className="profile-container">Error: {error}</div>;
  }

  if (!profileData) {
    return <div className="profile-container">Loading...</div>;
  }

  const formattedAddress = profileData.address
    ? profileData.address.split(",").map((line, index) => (
        <div key={index}>{line.trim()}</div>
      ))
    : "N/A";

  // ================= UI =================
  return (
    <div className="profile-container">
      {successMsg && <div className="success-message">{successMsg}</div>}
      {error && isEditing && <div className="error-message">{error}</div>}

      <div className="profile-details">
        {/* ================= PERSONAL DETAILS ================= */}
        <div className="profile-section">
          <h3>Personal Details</h3>

          <div className="profile-row">
            <strong>Employee ID:</strong>
            <span>{profileData.employee_id}</span>
          </div>

          <div className="profile-row">
            <strong>Name:</strong>
            <span>{profileData.name}</span>
          </div>

          <div className="profile-row">
            <strong>Email:</strong>
            <span>{profileData.email}</span>
          </div>

          <div className="profile-row">
            <strong>Primary Contact:</strong>
            <span>{profileData.phone}</span>
          </div>

          <div className="profile-row">
            <strong>Secondary Contact:</strong>
            {isEditing ? (
              <input
                type="text"
                name="secondary_phone"
                value={editableData.secondary_phone}
                onChange={handleChange}
                maxLength="10"
                placeholder="Enter 10-digit number"
              />
            ) : (
              <span>{profileData.secondary_phone || "N/A"}</span>
            )}
          </div>

          <div className="profile-row">
            <strong>Emergency Contact:</strong>
            {isEditing ? (
              <input
                type="text"
                name="emergency_contact"
                value={editableData.emergency_contact}
                onChange={handleChange}
                maxLength="10"
                placeholder="Enter 10-digit number"
              />
            ) : (
              <span>{profileData.emergency_contact || "N/A"}</span>
            )}
          </div>

          <div className="profile-row">
            <strong>Date of Birth:</strong>
            <span>{profileData.dob}</span>
          </div>

          <div className="profile-row">
            <strong>Joining Date:</strong>
            <span>{profileData.joining_date}</span>
          </div>

          <div className="profile-row">
            <strong>Gender:</strong>
            <span>{profileData.gender}</span>
          </div>

          <div className="profile-row address">
            <strong>Address:</strong>
            {isEditing ? (
              <textarea
                name="address"
                value={editableData.address}
                onChange={handleChange}
                rows="3"
              />
            ) : (
              <span>{formattedAddress}</span>
            )}
          </div>

          <div className="profile-row">
            <strong>Blood Group:</strong>
            <span>{profileData.blood_group}</span>
          </div>

          <div className="profile-row">
            <strong>Week Off:</strong>
            <span>
              {Array.isArray(profileData.week_off)
                ? profileData.week_off.join(", ")
                : profileData.week_off}
            </span>
          </div>
        </div>

        {/* ================= WORK DETAILS ================= */}
        <div className="profile-section">
          <h3>Work Details</h3>

          <div className="profile-row">
            <strong>Company:</strong>
            <span>{profileData.company || "Sparecode Consulting LLP"}</span>
          </div>

          <div className="profile-row">
            <strong>Designation:</strong>
            <span>{profileData.designation}</span>
          </div>

          <div className="profile-row">
            <strong>Department:</strong>
            <span>{profileData.department}</span>
          </div>

          <div className="profile-row">
            <strong>Reports To:</strong>
            <span>{profileData.report_to}</span>
          </div>

          <div className="profile-row">
            <strong>Work Type:</strong>
            <span>{profileData.work_type}</span>
          </div>
        </div>
      </div>

      {/* ================= ACTION BUTTONS ================= */}
      <div className="profile-actions">
        {!isEditing ? (
          <button className="edit-btn" onClick={handleEditClick}>
            Edit
          </button>
        ) : (
          <>
            <button className="save-btn" onClick={handleSave}>
              Save
            </button>
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
